import React, { useSyncExternalStore } from "react";
import AccessibleDialog from "../modal/AccessibleDialog";
import {
  getSessionState,
  signInAgain,
  signInOnThisPage,
  subscribeSession,
} from "../../services/session";
import "../../styles/SessionEndedDialog.scss";

/**
 * "Your session has ended": shown over the page when Keycloak no longer renews the sign-in, which
 * happens after the page sat idle or asleep for longer than the realm allows.
 *
 * The page stays as it is underneath, and whatever it was doing waits (see `services/session.ts`).
 * Signing in happens in a popup, so nothing on the page is lost; once it is done, the calls that
 * were turned away go out again by themselves. The dialog cannot be closed: without a session no
 * call on the page would work.
 */
const SessionEndedDialog: React.FC = () => {
  const state = useSyncExternalStore(subscribeSession, getSessionState);
  if (state === "active") return null;

  const signingIn = state === "signing-in";

  return (
    <AccessibleDialog
      title="Your session has ended"
      onClose={() => undefined}
      closeDisabled
      className="session-ended-dialog"
    >
      {signingIn ? (
        <p>
          Please finish signing in in the window that opened. This page stays as
          it is and carries on once you are signed in.
        </p>
      ) : (
        <p>
          You were signed out after a period of inactivity. Sign in again to
          continue: everything on this page stays as it is, and what you were
          doing carries on once you are signed in.
        </p>
      )}
      <div className="se-actions">
        {signingIn && (
          <button
            type="button"
            className="se-button is-secondary"
            onClick={() => void signInOnThisPage()}
          >
            Sign in on this page instead
          </button>
        )}
        <button
          type="button"
          className="se-button"
          onClick={() => void signInAgain()}
        >
          {signingIn ? "Open the sign-in window again" : "Sign in again"}
        </button>
      </div>
      {signingIn && (
        <p className="se-note">
          Signing in on this page reloads it. Your input is kept; files picked
          from your computer may need to be attached again.
        </p>
      )}
    </AccessibleDialog>
  );
};

export default SessionEndedDialog;
